import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getCheckins, createCheckin, getCheckinStreak, getCheckinStats, extractErrorMessage } from '../services/api';
import { FaPlus, FaFire, FaHeart } from 'react-icons/fa';
import UserSelect from '../components/UserSelect';
import { useToast } from '../components/Toast';
import '../styles/CheckIns.css';

function CheckIns({ user }) {
  const toast = useToast();
  const [checkins, setCheckins] = useState([]);
  const [streak, setStreak] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newCheckin, setNewCheckin] = useState({
    user_id: '',
    mood: 5,
    energy: 5,
    notes: '',
    subspace_level: 0
  });

  useEffect(() => {
    fetchCheckins();
  }, []);

  const fetchCheckins = async () => {
    try {
      setLoading(true);
      const data = await getCheckins();
      setCheckins(data.checkins || data);
    } catch (error) {
      console.error('Error fetching check-ins:', error);
      toast.error(extractErrorMessage(error, 'Check-Ins konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchUserStats = async (userId) => {
    try {
      const [streakData, statsData] = await Promise.all([
        getCheckinStreak(userId),
        getCheckinStats(userId)
      ]);
      setStreak(streakData);
      setStats(statsData);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error(extractErrorMessage(error, 'Statistiken konnten nicht geladen werden'));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createCheckin(newCheckin);
      setNewCheckin({ user_id: '', mood: 5, energy: 5, notes: '', subspace_level: 0 });
      setShowModal(false);
      toast.success('Check-In erstellt');
      fetchCheckins();
    } catch (error) {
      console.error('Error creating check-in:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen des Check-Ins'));
    } finally {
      setSaving(false);
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getMoodEmoji = (mood) => {
    if (mood >= 8) return '😊';
    if (mood >= 6) return '🙂';
    if (mood >= 4) return '😐';
    if (mood >= 2) return '😔';
    return '😢';
  };

  const getEnergyColor = (energy) => {
    if (energy >= 8) return '#43b581';
    if (energy >= 5) return '#faa61a';
    return '#f04747';
  };

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Check-Ins</h1>
          <button className="add-btn" onClick={() => setShowModal(true)}>
            <FaPlus /> Neuer Check-In
          </button>
        </div>

        {streak && (
          <div className="stats-cards">
            <div className="stat-card streak">
              <span className="stat-value"><FaFire /> {streak.current_streak || 0}</span>
              <span className="stat-label">Aktuelle Serie</span>
            </div>
            <div className="stat-card">
              <span className="stat-value">{streak.longest_streak || 0}</span>
              <span className="stat-label">Längste Serie</span>
            </div>
            {stats && (
              <>
                <div className="stat-card mood-stat">
                  <span className="stat-value">{stats.avg_mood ? Number(stats.avg_mood).toFixed(1) : '-'}</span>
                  <span className="stat-label">Durchschn. Stimmung</span>
                </div>
                <div className="stat-card energy-stat">
                  <span className="stat-value">{stats.avg_energy ? Number(stats.avg_energy).toFixed(1) : '-'}</span>
                  <span className="stat-label">Durchschn. Energie</span>
                </div>
              </>
            )}
          </div>
        )}

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="checkins-list">
            {checkins.length > 0 ? (
              checkins.map(ci => (
                <div key={ci.id} className="checkin-card">
                  <div className="checkin-header">
                    <span className="checkin-date">
                      {new Date(ci.created_at || ci.timestamp).toLocaleDateString('de-DE', {
                        weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
                      })}
                    </span>
                    <span className="checkin-time">
                      {new Date(ci.created_at || ci.timestamp).toLocaleTimeString('de-DE', {
                        hour: '2-digit', minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div className="checkin-meters">
                    <div className="meter">
                      <span className="meter-label">Stimmung</span>
                      <div className="meter-bar">
                        <div className="meter-fill mood" style={{ width: `${(ci.mood / 10) * 100}%` }}></div>
                      </div>
                      <span className="meter-value">{getMoodEmoji(ci.mood)} {ci.mood}/10</span>
                    </div>
                    <div className="meter">
                      <span className="meter-label">Energie</span>
                      <div className="meter-bar">
                        <div className="meter-fill energy" style={{ width: `${(ci.energy / 10) * 100}%`, background: getEnergyColor(ci.energy) }}></div>
                      </div>
                      <span className="meter-value">{ci.energy}/10</span>
                    </div>
                    {ci.subspace_level > 0 && (
                      <div className="meter">
                        <span className="meter-label">Subspace</span>
                        <div className="meter-bar">
                          <div className="meter-fill subspace" style={{ width: `${(ci.subspace_level / 10) * 100}%` }}></div>
                        </div>
                        <span className="meter-value">{ci.subspace_level}/10</span>
                      </div>
                    )}
                  </div>
                  {ci.notes && <p className="checkin-notes">{ci.notes}</p>}
                </div>
              ))
            ) : (
              <div className="no-data">Keine Check-Ins gefunden</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neuer Check-In</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreate}>
                <div className="form-group">
                  <UserSelect
                    value={newCheckin.user_id}
                    onChange={id => setNewCheckin({...newCheckin, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Stimmung: {newCheckin.mood}/10 {getMoodEmoji(newCheckin.mood)}</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newCheckin.mood}
                    onChange={e => setNewCheckin({...newCheckin, mood: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Energie: {newCheckin.energy}/10</label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={newCheckin.energy}
                    onChange={e => setNewCheckin({...newCheckin, energy: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Subspace Level: {newCheckin.subspace_level}/10</label>
                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={newCheckin.subspace_level}
                    onChange={e => setNewCheckin({...newCheckin, subspace_level: parseInt(e.target.value)})}
                    className="slider"
                  />
                </div>
                <div className="form-group">
                  <label>Notizen</label>
                  <textarea
                    value={newCheckin.notes}
                    onChange={e => setNewCheckin({...newCheckin, notes: e.target.value})}
                    placeholder="Wie geht es dir heute?"
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert\u2026' : 'Einchecken'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default CheckIns;
